<template>
  <div
    v-if="report"
    class="q-ma-sm report"
  >
    <div
      v-if="!printing && !simpleView"
      class="row items-center justify-between"
    >
      <breadcrumbs
        class="col"
        :name="report.name"
      />
      <support-menu
        category="Reports"
        class="col-auto"
      />
    </div>
    <div
      v-if="!printing && !simpleView && report && report.reportOptions"
      class="col row justify-end items-center q-gutter-md q-mb-lg report-header"
    >
      <h5 class="col-auto q-my-md">
        {{ report.title }}
      </h5>
      <q-badge
        v-if="report.atlas"
        class="col-auto q-my-md q-px-sm q-py-xs"
        color="blue-8"
        outline
      >
        ESSENTIAL
        <q-tooltip>Essential Report</q-tooltip>
      </q-badge>
      <q-badge
        v-if="report.user"
        class="col-auto q-my-md q-px-sm q-py-xs"
        color="blue-5"
        outline
      >
        PERSONAL
        <q-tooltip>This is a personal report</q-tooltip>
      </q-badge>
      <report-status
        class="q-ml-md q-mb-md"
        :report-status="report.reportStatus"
        :class="{ 'data-freshness': report.reportStatus && report.reportStatus.done }"
      />
      <q-space />
      <template v-if="report && report.reportOptions">
        <q-btn
          v-if="!showSearchField"
          class="col-auto search-button q-px-sm"
          icon="search"
          round
          @click="toggleSearch"
        >
          <q-tooltip>Search Report</q-tooltip>
        </q-btn>
        <q-input
          v-if="showSearchField"
          ref="searchInput"
          :model-value="searchQuery"
          class="col-auto search-input"
          borderless
          dense
          placeholder="Search..."
          style="width: 200px"
          autofocus
          @update:model-value="$_debouncedSearchUpdate"
        >
          <template #append>
            <q-icon
              name="close"
              size="xs"
              class="cursor-pointer"
              style="margin-right: 4px"
              @click="clearSearch"
            />
          </template>
        </q-input>
        <report-control
          v-for="control in report.controls"
          :key="control"
          :report="report"
          :control="control"
          :inventory-recipes="inventoryRecipes"
          :studios="studios"
          :users="users"
          class="col-auto q-ma-md"
          @options-changed="handleOptionsChange()"
        />
        <report-filter-dropdowns
          :key="`report-filters-${JSON.stringify(report.reportOptions.report_filters || [])}`"
          v-model="showFilterDropdown"
          :report="report"
          class="col-auto q-ma-md"
          @filters-changed="report.runReport()"
        />
      </template>
      <report-context-menu
        class="col-auto q-mt-sm"
        :report="report"
        :copied-element-ids="copiedElementIds"
        :copied-element="copiedElement"
        @edit-report="$refs.reportForm.show(report)"
        @add-element="addElement()"
        @edit-filters="showReportFilters = true"
        @paste-elements="pasteElements()"
        @edit="val => editing = val"
      />
    </div>
    <div
      v-else-if="printing && report && report.pdfHeader"
      class="row justify-between q-mx-sm items-center"
    >
      <div class="col-auto">
        <h5 class="q-ma-none">
          {{ report.title }}
        </h5>
        <h6
          v-if="report.subtitle"
          class="q-ma-none"
        >
          {{ report.subtitle }}
        </h6>
      </div>
      <div class="col-auto text-center">
        <img
          :src="report.logoImageUrl"
          style="max-height: 70px"
        >
      </div>
    </div>
    <div
      v-if="!printing && simpleView && report && report.reportStatus && !report.reportStatus.done"
      class="row justify-center"
    >
      <report-status
        class="q-ma-xl"
        :report-status="report.reportStatus"
        :class="{ 'data-freshness': report.reportStatus && report.reportStatus.done }"
      />
    </div>
    <transition-group
      name="list-complete"
      :class="{ 'animate': !printing }"
      tag="span"
      style="vertical-align: top; display: block;"
      @click="unselectElements()"
    >
      <report-element
        v-for="element in filteredElements"
        :key="element.id"
        v-model:search-query="searchQuery"
        :report-element="element"

        :report="report"
        :editing="editing"
        :printing="printing"
        :simple-view="simpleView"

        :selected-element-ids="selectedElementIds"
        :copied-element-ids="copiedElementIds"
        :copied-element="copiedElement"

        :draggable="editing"

        @drag-start="index => dragStartId = index"
        @drag="dragging"
        @drag-end="(index, dropAfter) => dragTo(index, dropAfter)"

        @edit="val => editing = val"
        @edit-element="showEditElement(element.id)"
        @advanced-edit-element="advancedEditElement(element)"

        @select-element="element => selectElement(element)"
        @unselect-elements="unselectElements()"

        @copy-elements="ids => copyElements(ids)"
        @paste-elements="startElement => pasteElements(startElement)"
        @paste-special="type => pasteSpecial(element, type)"

        @duplicate-elements="ids => duplicateElements(ids)"
        @delete-elements="ids => deleteElements(ids)"
      />
    </transition-group>
    <report-form
      v-if="editing"
      ref="reportForm"
    />
    <report-element-preview-form
      ref="reportElementPreviewForm"
      :report="report"
      :report-element="editElement"
    />
    <report-element-form
      v-if="editing"
      ref="reportElementForm"
      :report="report"
      :editing="true"
      @model-created="report.runReport()"
    />
    <report-filters-dialog
      v-model="showReportFilters"
      :report-element="report"
      @edit-filter="field => editFilters(field)"
    />

    <q-inner-loading :showing="report.initialLoading && !printing || reordering" />

    <!-- This id is used by PDF Crowd to know when the page is done rendering -->
    <div
      v-if="report && report.reportStatus && report.reportStatus.done && readyToPrint"
      id="render-complete"
    />
  </div>
</template>

<script>

import { exportFile, Notify } from 'quasar'
import { CURRENCY_TO_LOCALE_MAP, DEFAULT_CURRENCY } from 'src/i18n'
import Report, { REPORT_ACTIONS } from 'src/models/report/Report'
import ReportElementModel from 'src/models/report/ReportElement'
import Breadcrumbs from 'src/modules/admin/components/Breadcrumbs'
import ReportContextMenu from 'src/modules/admin/report/ReportContextMenu'
import ReportControl from 'src/modules/admin/report/ReportControl'
import ReportElement from 'src/modules/admin/report/ReportElement'
import ReportElementForm from 'src/modules/admin/report/ReportElementForm'
import ReportElementPreviewForm from 'src/modules/admin/report/ReportElementPreviewForm'
import ReportFilterDropdowns from 'src/modules/admin/report/ReportFilterDropdowns'
import ReportFiltersDialog from 'src/modules/admin/report/ReportFiltersDialog'
import ReportForm from 'src/modules/admin/report/ReportForm'
import ReportStatus from 'src/modules/admin/report/ReportStatus'
import SupportMenu from 'src/modules/admin/support/SupportMenu'

function wrapCsvValue (val) {
  let formatted = val

  formatted = formatted === null ? '' : String(formatted)

  formatted = formatted.split('"').join('""').split('\n').join('\\n').split('\r').join('\\r')

  return `"${formatted}"`
}

export default {
  name: 'Report',
  components: {
    Breadcrumbs,
    ReportElement,
    ReportStatus,
    ReportContextMenu,
    ReportControl,
    ReportForm,
    ReportElementForm,
    ReportElementPreviewForm,
    ReportFiltersDialog,
    SupportMenu,
    ReportFilterDropdowns
  },
  props: {
    systemReport: {
      type: String,
      required: false,
      default: null
    },
    options: {
      type: Object,
      required: false,
      default: null
    },
    elementOptions: {
      type: Object,
      required: false,
      default: null
    },
    forceRecomputeKey: {
      type: Number,
      required: false,
      default: null
    },
    atlas: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      sendingEmail: false,
      editing: false,
      dragStartId: null,
      selectedElementIds: [],
      copiedElementIds: [],
      simpleView: false,
      editElementId: null,
      reordering: false,
      showEditFilter: false,
      showFilterDropdown: true,
      editFilterField: null,
      showReportFilters: false,
      report: null,
      readyToPrint: false,
      searchQuery: '',
      showSearchField: false
    }
  },
  models: {
    inventoryRecipes: {
      variables () {
        return {
          limit: 0,
          skip: this.printing
        }
      }
    },
    studios: {
      variables () {
        return {
          limit: 1000,
          skip: this.printing
        }
      }
    },
    users: {
      fields: ['id', 'firstName', 'lastName', 'authorizedSiteIds'],
      variables () {
        return {
          userRole: 'employees',
          limit: 1000,
          skip: this.printing
        }
      }
    }
  },
  computed: {
    printing () {
      return _.endsWith(this.$route.name, ':print-report')
    },
    editElement () {
      return _.find(this.report.elements.data, { id: this.editElementId })
    },
    // Copied elements has to be an element still in this report
    copiedElement () {
      if (!this.editing) return null

      return _.find(this.report.elements.data, { id: this.copiedElementIds[0] })
    },
    filteredElements () {
      const baseElements = this.editing ? this.report.elements.data : _.filter(this.report.elements.data, { hidden: false })
      return baseElements
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  watch: {
    'report.reportOptions': {
      deep: true,
      handler () {
        if (this.simpleView) return

        this.updateRoute()
      }
    },
    options: {
      deep: true,
      handler () {
        if (!this.report) return
        this.report.reportOptions = this.options
        this.report.runReport()
      }
    },
    elementOptions: {
      deep: true,
      handler () {
        _.each(this.report.elements.data, element => {
          element.reportOptions = this.elementOptions
        })
        this.report.runReport()
      }
    },
    forceRecomputeKey () {
      this.report.forceRecompute()
    },
    '$route.name' () {
      this.initializeReport()
    },
    '$route.params.id' () {
      this.initializeReport()
    },
    'currentSite.id' () {
      this.runReportWithAction()
    }
  },
  async created () {
    const Highcharts = await this.$getHighchartsAsync()

    if (this.printing) {
      document.body.style = 'background: none;'
      Highcharts.setOptions({
        plotOptions: {
          series: {
            animation: false
          }
        }
      })
    }

    this.$_debouncedSearchUpdate = _.debounce((value) => {
      this.searchQuery = value
    }, 280)

    this.initializeReport()
  },
  unmounted () {
    if (this.report) {
      this.report.destroy()
    }
  },
  methods: {
    async initializeReport () {
      let reportId
      if (this.$props.systemReport) {
        reportId = this.currentCircuit.reportIdForName(this.$props.systemReport)
        if (!reportId) throw new Error(`Unknown System Report Name ${this.$props.systemReport}`)
        this.simpleView = true
      } else if (this.$route.name === 'admin:dashboard') {
        reportId = this.currentUser.customDashboard?.id || this.currentSite.defaultDashboard?.id || this.currentCircuit.defaultDashboard?.id
        this.simpleView = true
      } else {
        reportId = this.$route.params.id
        this.simpleView = false
      }

      if (!reportId) {
        this.$router.push('/operations')
        return
      }
      if (this.report) {
        this.report.destroy()
      }

      // For system reports, use regular report context
      const isAtlasReport = this.$props.systemReport ? false : (this.atlas || this.$route.query.atlas === 'true')

      this.report = new Report({
        id: reportId,
        reportOptions: {},
        atlas: isAtlasReport
      }, {
        watchQuery: false,
        controls: 'default',
        reportOptions: 'default',
        defaultOptions: 'default',
        initialOptions: 'default',
        possibleOptionsValues: 'default',
        user: 'default',
        elements: {
          reportStatus: 'default',

          controls: 'default',
          possibleOptionsValues: 'default',

          chart: {
            columns: 'default'
          },
          table: {
            columns: 'default'
          },
          view: 'default',
          widget: {
            columns: 'default',
            sparkline: 'default'
          },
          html: 'default',
          rawData: 'default'
        },
        reportStatus: 'default'
      })

      // Use the passed in initial options
      if (this.options) {
        this.report.reportOptions = this.options
      }

      // Apply query parameters
      if (!this.options && _.keys(this.$route.query).length > 0) {
        _(this.$route.query).keys().each((key) => {
          if (this.$route.query[key] === 'true') {
            this.report.reportOptions[key] = true
          } else if (this.$route.query[key] === 'false') {
            this.report.reportOptions[key] = false
          } else {
            this.report.reportOptions[key] = this.$route.query[key]
          }
        })
      }

      if (this.$route.query.token) {
        this.report.token = this.$route.query.token
      } else if (!this.options && !this.elementOptions) {
        // If there is no token, fetch at this point so initial options get applied
        // prior to running the report
        await this.report.fetch()
      }

      // Need set options on the report element prior to running the report
      if (this.elementOptions) {
        if (this.elementOptions && !this.options) {
          // Temporarily set the report options to the element options for loading the initial values
          this.report.reportOptions = this.elementOptions
          await this.report.fetch()
          // Need to unset the report options here so the element options get applied and element is editable
          this.report.reportOptions = {}
        } else {
          await this.report.fetch()
        }

        // Set the options on the elements
        _.each(this.report.elements.data, element => {
          element.reportOptions = this.elementOptions
        })
      }

      await this.runReportWithAction()

      if (this.$route.query.token) {
        // If we are running this report via a token,
        // we need to refetch the configuration
        await this.report.configuration.validate()
      }

      if (!this.currentCircuit) {
        // TODO INDY-3193: How should we handle this? Also this never reverts the locale change.
        this.$i18n.locale = CURRENCY_TO_LOCALE_MAP[this.report.currency || DEFAULT_CURRENCY]
      }

      this.startPollingForPrintableStatus()

      if (localStorage.copiedElementIds) {
        this.copiedElementIds = JSON.parse(localStorage.copiedElementIds)
      }
    },
    /**
     * Start polling up to a timeout to either:
     * - trigger window.print()
     * - render the #render-complete element for PDF Crowd
     *
     * Ideally we should only poll up PDFCrowd's max timeout (10s), but note
     * that some large reports may not finish rendering all table
     * components within that timeframe.
     */
    startPollingForPrintableStatus () {
      const pollEveryMs = 100
      const maxPollCount = 200
      let pollCount = 0

      const printOrQueuePrint = () => {
        pollCount += 1
        const printableCount = document.querySelectorAll('[data-printable]').length
        const readyToPrintCount = document.querySelectorAll('[data-printable="ready"]').length

        if (
          (this.report.reportStatus?.done && (printableCount === 0 || printableCount === readyToPrintCount)) ||
          pollCount > maxPollCount
        ) {
          // For PDF Crowd
          this.readyToPrint = true

          if (this.$route.query.print) {
            window.print()
          }
        } else {
          setTimeout(printOrQueuePrint, pollEveryMs)
        }
      }

      setTimeout(printOrQueuePrint, pollEveryMs)
    },
    updateRoute () {
      // Filter out filter-related options
      const filteredOptions = _.omitBy(this.report.reportOptions, (value, key) => {
        return key.endsWith('_filter') || key.startsWith('exclude_') || key === 'report_filters'
      })

      let query = { ...this.$route.query, ...filteredOptions }
      query = _.omitBy(query, _.isNil)

      // NOTE: using the native history API allows us to update URL params without causing a vue-router navigation,
      // but it also causes this.$route.query to not be updated. It will retain the initial pageload's query.
      history.replaceState(
        {},
        null,
        '#' + this.$route.path + '?' +
          _(query).keys().map(key => encodeURIComponent(key) + '=' + encodeURIComponent(query[key])).value().join('&')
      )
    },
    exportData (reportElement) {
      const content = _.map(reportElement.rawData, row => _.map(row, cell => wrapCsvValue(cell)).join(',')).join('\r\n')

      exportFile(
        `${this.report.fullTitle} - ${reportElement.title}.csv`,
        content,
        'text/csv'
      )
    },
    async handleChange () {
      this.updateRoute()
      await this.report.runReport()
    },
    handleOptionsChange () {
      if (this.saveDebounceTimer) return

      this.saveDebounceTimer = true
      setTimeout(async () => {
        await this.handleChange()
        this.saveDebounceTimer = false
      })
    },
    dragging (event) {
      // If the element is dragged close to the top, scroll up
      if (event.y < 80) {
        window.scrollBy(0, -5)
      }
    },
    async dragTo (dragEndId, dropAfter = false) {
      if (this.dragStartId === null || dragEndId === null || this.dragStartId === dragEndId) return

      const movingElement = _.find(this.report.elements.data, { id: this.dragStartId })
      const dropElement = _.find(this.report.elements.data, { id: dragEndId })

      if (!movingElement || !dropElement) return

      // Do move by setting value between the two and the backend does the reorder on save
      movingElement.displayOrder = dropElement.displayOrder + (dropAfter ? 5 : -5)
      const movingElementCopy = new ReportElementModel({
        id: movingElement.id,
        displayOrder: movingElement.displayOrder,
        atlas: this.report.atlas
      }, {
        fields: ['id', 'displayOrder', 'atlas']
      })

      this.reordering = true
      await movingElementCopy.save()
      movingElementCopy.destroy()

      // Rerun the report to get the update
      await this.report.runReport()
      this.reordering = false

      this.dragStartId = null
    },
    addElement () {
      this.$refs.reportElementForm.show(null, { reportId: this.report.id, atlas: this.report.atlas })
    },
    showEditElement (reportElementId) {
      this.editElementId = reportElementId
      this.$refs.reportElementPreviewForm.show()
    },
    advancedEditElement (reportElement) {
      this.$refs.reportElementForm.show(reportElement, { atlas: this.report.atlas })
    },
    selectElement (reportElementId) {
      if (_.includes(this.selectedElementIds, reportElementId)) {
        this.selectedElementIds = _.filter(this.selectedElementIds, id => id !== reportElementId)
      } else {
        this.selectedElementIds.push(reportElementId)
      }
    },
    unselectElement (reportElementId) {
      this.selectedElementIds = _.filter(this.selectedElementIds, id => id !== reportElementId)
    },
    unselectElements () {
      this.selectedElementIds = []
    },
    async deleteElements (ids) {
      for (const id of ids) {
        const model = new ReportElementModel({
          id,
          atlas: this.report.atlas
        })
        const { errors } = await model.delete()
        if (errors && errors.length > 0) {
          Notify.create({
            message: 'Error deleting',
            type: 'negative',
            position: 'top'
          })
          console.log(errors)
        }
      }
      this.report.runReport()
      this.selectedElementIds = []
    },
    async duplicateElements (ids) {
      const model = new ReportElementModel()
      const { errors } = await model.duplicate(ids, this.report.atlas)
      if (errors && errors.length > 0) {
        Notify.create({
          message: 'Error duplicating',
          type: 'negative',
          position: 'top'
        })
        console.log(errors)
      }
      this.report.runReport()
      this.selectedElementIds = []
    },
    async pasteElements (startElement) {
      const model = new ReportElementModel()
      const result = await model.duplicate(this.copiedElementIds, this.report.atlas)
      const errors = result.errors
      if (errors && errors.length > 0) {
        Notify.create({
          message: 'Error Pasting',
          type: 'negative',
          position: 'top'
        })
        console.log(errors)
      } else {
        // Set the properties of the newly created elements to be in this position and in this report
        let resultElements = result.data.reportElements

        // If there is a start element, we need to reverse the list since we set the display order
        // to match the starting element and with each save, the display ids are updated on the backend
        // this ensures the order is preseved. It's the opposite for the scenario when we are adding
        // to the end, 9999 will be used for each one so order will be preserved
        if (startElement) {
          resultElements = resultElements.reverse()
        }
        for (const newElementProps of resultElements) {
          const newElement = new ReportElementModel({
            id: newElementProps.id,
            displayOrder: startElement ? (startElement.displayOrder - 5) : 9999,
            reportId: this.report.id,
            atlas: this.report.atlas
          }, {
            fields: ['id', 'displayOrder', 'reportId', 'atlas']
          })
          await newElement.save()
        }
        this.report.runReport()
      }
    },
    async pasteSpecial (destinationElement, type) {
      if (type === 'columns') {
        destinationElement.reportOptions.column_order = [...this.copiedElement.reportOptions.column_order]
      } else if (type === 'filters') {
        destinationElement.matchFilters(this.copiedElement.reportOptions)
      } else if (type === 'groupings') {
        destinationElement.reportOptions.group_by = [...this.copiedElement.reportOptions.group_by]
      } else {
        throw new Error('Unhandled Paste Special Type: ' + type)
      }

      this.report.runReport()
    },
    copyElements (ids) {
      this.copiedElementIds = ids
      this.selectedElementIds = []

      localStorage.copiedElementIds = JSON.stringify(this.copiedElementIds)
    },
    async editFilters (field) {
      this.editFilterField = field
      this.showEditFilter = true
    },
    toggleSearch () {
      console.log('toggleSearch')
      this.showSearchField = !this.showSearchField
      if (!this.showSearchField) {
        this.searchQuery = ''
      } else {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      }
    },
    clearSearch () {
      this.searchQuery = ''
      this.showSearchField = false
    },
    runReportWithAction () {
      const reportAction = REPORT_ACTIONS.RUN_REPORT
      return this.report.runReport(reportAction)
    }
  }
}
</script>
<style lang="scss" scoped>
.animate {
  .list-complete-item {
    transition: all 0.5s;
    display: inline-block;
  }
  .list-complete-enter {
    opacity: 0;
    transform: translateX(0) translateY(0);
  }
  .list-complete-leave-to {
    opacity: 0;
    transform: translateX(0) translateY(0);
  }
  .list-complete-leave-active {
    position: absolute;
  }
}

.search-input {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  margin-right: 8px;
}
</style>
<style>
  .report-header .data-freshness {
    visibility: hidden;
  }

  .report-header:hover .data-freshness {
    visibility: visible;
  }
</style>
